/**
 * 搜索服务
 * 提供帖子内容、用户名、Agent名的搜索功能
 */

import { supabase } from './supabase'

// 搜索结果类型（与 v_feed 视图结构一致）
export interface SearchResult {
  id: string
  content: string | null
  images: string[] | null
  tags: string[] | null
  likes_count: number
  comments_count: number
  created_at: string
  author_id: string
  author_type: 'human' | 'agent'
  author_name: string | null
  author_avatar: string | null
  author_bio: string | null
}

// 分页结果类型
export interface PaginatedResult {
  data: SearchResult[]
  nextCursor: string | null
  hasMore: boolean
}

/**
 * 获取 Feed 数据 - 支持分页
 * @param pageSize 每页数量
 * @param cursor 分页游标（created_at）
 */
export async function getFeedPaginated(
  pageSize: number = 20,
  cursor?: string
): Promise<PaginatedResult> {
  let query = supabase
    .from('v_feed')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(pageSize + 1) // 多获取一条来判断是否有更多数据

  // 如果有游标，添加游标条件
  if (cursor) {
    query = query.lt('created_at', cursor)
  }

  const { data, error } = await query

  if (error) throw error

  const hasMore = (data?.length || 0) > pageSize
  const items = hasMore ? data?.slice(0, pageSize) : data

  return {
    data: items || [],
    nextCursor: items && items.length > 0 ? items[items.length - 1].created_at : null,
    hasMore,
  }
}

/**
 * 搜索帖子 - 支持搜索帖子内容、用户名、Agent名，支持分页
 * @param query 搜索关键词
 * @param pageSize 每页数量
 * @param cursor 分页游标（created_at）
 */
export async function searchPostsPaginated(
  query: string,
  pageSize: number = 20,
  cursor?: string
): Promise<PaginatedResult> {
  if (!query.trim()) {
    return getFeedPaginated(pageSize, cursor)
  }

  // 将搜索词分词，用于多字段搜索
  const searchTerms = query.trim().split(/\s+/).filter(Boolean)

  // 构建搜索条件
  let queryBuilder = supabase
    .from('v_feed')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(pageSize + 1)

  // 如果有游标，添加游标条件
  if (cursor) {
    queryBuilder = queryBuilder.lt('created_at', cursor)
  }

  // 使用 or 条件组合多个搜索字段
  const orConditions = searchTerms
    .map(term => {
      const escapedTerm = term.replace(/[%_]/g, '\\$&')
      return `content.ilike.%${escapedTerm}%,author_name.ilike.%${escapedTerm}%`
    })
    .join(',')

  const { data, error } = await queryBuilder.or(orConditions)

  if (error) throw error

  const hasMore = (data?.length || 0) > pageSize
  const items = hasMore ? data?.slice(0, pageSize) : data

  return {
    data: items || [],
    nextCursor: items && items.length > 0 ? items[items.length - 1].created_at : null,
    hasMore,
  }
}

/**
 * 搜索帖子 - 支持搜索帖子内容、用户名、Agent名
 * @param query 搜索关键词
 * @param limit 返回结果数量限制
 */
export async function searchPosts(query: string, limit: number = 50): Promise<SearchResult[]> {
  if (!query.trim()) {
    // 如果查询为空，返回最新的帖子
    const { data, error } = await supabase
      .from('v_feed')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // 将搜索词分词，用于多字段搜索
  const searchTerms = query.trim().split(/\s+/).filter(Boolean)

  // 构建搜索条件：搜索帖子内容或作者名
  // 使用 ilike 进行模糊匹配，每个词都需要匹配
  let queryBuilder = supabase
    .from('v_feed')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)

  // 使用 or 条件组合多个搜索字段
  // 对每个搜索词，需要匹配 content、author_name 中的任意一个
  const orConditions = searchTerms.map(term => {
    const escapedTerm = term.replace(/[%_]/g, '\\$&') // 转义特殊字符
    return `content.ilike.%${escapedTerm}%,author_name.ilike.%${escapedTerm}%`
  }).join(',')

  // 使用 or 过滤器
  const { data, error } = await queryBuilder.or(orConditions)

  if (error) throw error
  return data || []
}

/**
 * 使用全文搜索搜索帖子内容
 * 性能更好，但只能搜索帖子内容，不能搜索作者名
 * @param query 搜索关键词
 * @param limit 返回结果数量限制
 */
export async function searchPostsByTextSearch(query: string, limit: number = 50): Promise<SearchResult[]> {
  if (!query.trim()) {
    const { data, error } = await supabase
      .from('v_feed')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // 预处理搜索词：将空格替换为 &（AND 搜索）
  const processedQuery = query.trim().split(/\s+/).join(' & ')

  const { data, error } = await supabase
    .from('v_feed')
    .select('*')
    .textSearch('content', processedQuery, {
      type: 'websearch',
      config: 'simple'
    })
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    // 如果全文搜索失败，回退到 ilike 搜索
    console.warn('Full-text search failed, falling back to ilike search:', error)
    return searchPosts(query, limit)
  }

  return data || []
}